import React, { Component } from "react";
import moment from "moment";

import GameService from "../services/game";
import "./SurvivorClockBar.css";

/**
 * The Survivor QA clock, on screen. See pt3server/pt3backendapi/common/clock.py.
 *
 * Two jobs, and the second is the important one:
 *
 *   1. with `control`, lets a tester set the apparent date, so an eighteen-week
 *      season can be walked in an afternoon rather than eighteen weeks;
 *   2. whenever the clock has been moved, says so — loudly, on every Survivor
 *      screen, whether or not that screen can change it.
 *
 * The banner is not decoration. The one genuine hazard in this tool is somebody
 * forgetting it is on: demonstrating to a customer on a date that is not today,
 * or reporting a bug that only exists three weeks from now. It renders nothing
 * at all when the override is disabled, which is everywhere but a developer's
 * machine.
 *
 * It collapses, for screen capture. A recording of the pick screen should not
 * be four fifths hazard stripes -- but the clock never hides completely, because
 * the reason it is loud does not go away when a camera is running. Collapsed, it
 * is the apparent date and nothing else, and that date is the button that brings
 * the controls back. Collapse lasts the browser session: it has to survive the
 * reload that setting the clock performs, and it must not survive the tester
 * going home.
 */
const COLLAPSE_KEY = "survivorClockCollapsed";

function rememberedCollapse(){
    try { return window.sessionStorage.getItem(COLLAPSE_KEY) === "1"; }
    catch (e) { return false; }   /* private mode, embedded webview */
}

class SurvivorClockBar extends Component {
    constructor(props){
        super(props);
        this.state = {
            clock: null, typed: "", busy: false,
            collapsed: rememberedCollapse()
        };
    }

    setCollapsed(collapsed){
        this.setState({collapsed: collapsed});
        try { window.sessionStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); }
        catch (e) { /* the toggle still works, it just forgets on reload */ }
    }

    componentDidMount(){
        GameService.getSurvivorClock().then(
            (clock) => this.setState({
                clock: clock,
                // Pre-filled with where the clock currently stands, so moving a
                // week on is an edit rather than a retype.
                typed: moment(clock.apparent_now).format("YYYY-MM-DDTHH:mm")
            }),
            () => { /* the bar simply does not appear */ }
        );
    }

    /** A full reload, deliberately. Moving the clock changes what almost every
     *  request would answer, and re-fetching one screen's data would leave the
     *  rest of the session reasoning about the old date. */
    apply(iso){
        this.setState({busy: true});
        GameService.setSurvivorClock(iso).then(
            () => window.location.reload(),
            () => this.setState({busy: false})
        );
    }

    render(){
        let clock = this.state.clock;
        if(!clock) return null;
        if(!clock.enabled && !clock.active) return null;

        let apparent = moment(clock.apparent_now);

        if(this.state.collapsed){
            return (
                <button type="button"
                        className={"qaclock-chip" + (clock.active ? " qaclock-chip-active" : "")}
                        title="Test clock — click for the controls"
                        onClick={() => this.setCollapsed(false)}>
                    {apparent.format("ddd D MMM YYYY, h:mm A")}
                </button>
            );
        }

        return (
            <div className={"qaclock" + (clock.active ? " qaclock-active" : "")}>
                {clock.active ? (
                    <span className="qaclock-warning">
                        ⚠ TEST CLOCK — the game believes it is{" "}
                        <strong>{apparent.format("ddd D MMM YYYY, h:mm A")}</strong>
                    </span>
                ) : (
                    <span className="qaclock-idle">Test clock available</span>
                )}

                {this.props.control && clock.enabled ? (
                    <span className="qaclock-controls">
                        <input
                            type="datetime-local"
                            value={this.state.typed}
                            disabled={this.state.busy}
                            onChange={(e) => this.setState({typed: e.target.value})}
                        />
                        <button type="button" disabled={this.state.busy}
                                onClick={() => this.apply(this.state.typed)}>
                            Set
                        </button>
                        <button type="button" disabled={this.state.busy || !clock.active}
                                onClick={() => this.apply(null)}>
                            Real time
                        </button>
                        {/* The move a tester actually makes, over and over. */}
                        <button type="button" disabled={this.state.busy}
                                onClick={() => this.apply(
                                    apparent.clone().add(7, "days").format("YYYY-MM-DDTHH:mm")
                                )}>
                            +1 week
                        </button>
                    </span>
                ) : null}

                {/* Always offered, control or not: every Survivor screen carries
                    this bar, and any of them might be the one being filmed. */}
                <button type="button" className="qaclock-hide"
                        title="Hide the controls for recording — the date stays"
                        onClick={() => this.setCollapsed(true)}>
                    Hide
                </button>
            </div>
        );
    }
}

export default SurvivorClockBar;
